import * as React from 'react';
import Chart from 'chart.js';
import 'chartjs-chart-treemap';
import { themeDark } from 'styles/theme';
import abbreviateNumber from 'utils/shared/number/abbreviateNumber';
import { colorFromValue } from 'utils/client/chart';
import { SectorGraphProps } from './props';

type SectorTreemapSource = {
  sector: string;
  sentimentScore: number;
  code: number;
  premium: number;
  OTM: number;
};

type SectorTreemapDataPoint = {
  _data: SectorTreemapSource;
  v: number;
};

type SectorTreemapDataset = Chart.ChartDataSets & {
  tree: SectorTreemapSource[];
  key: string;
  groups: string[];
  spacing: number;
  data?: SectorTreemapDataPoint[];
};

type SectorTreemapContext = {
  dataset: SectorTreemapDataset;
  dataIndex: number;
};

const getTreemapTooltipItem = (item: Chart.ChartTooltipItem, data: Chart.ChartData) => {
  if (
    typeof item.index === 'undefined' ||
    typeof item.datasetIndex === 'undefined' ||
    !data.datasets
  ) {
    return null;
  }

  const dataset = data.datasets[item.datasetIndex] as SectorTreemapDataset | undefined;
  return dataset?.data?.[item.index] || null;
};

const formatTreemapTooltip = (
  dataItem: SectorTreemapDataPoint | null,
  metricLabel: 'Net Premium' | 'Premium',
) => {
  if (!dataItem) {
    return '';
  }

  return `${dataItem._data.sector} ${metricLabel}: $${abbreviateNumber(dataItem._data.premium)}`;
};

const mapSectorToBackgroundColor = (
  sentimentScore: number,
  isUnusualFiltered: boolean,
  isSweepsFiltered: boolean,
) => {
  let callColor = themeDark.color.graphCall;
  let putColor = themeDark.color.graphPut;

  if (isUnusualFiltered) {
    callColor = themeDark.color.graphUnusualCall;
    putColor = themeDark.color.graphUnusualPut;
  } else if (isSweepsFiltered) {
    callColor = themeDark.color.graphSweep;
    putColor = themeDark.color.graphSweep;
  }

  if (sentimentScore > 10) {
    return callColor;
  } else if (sentimentScore < -10) {
    return putColor;
  }

  return themeDark.color.mixedSentiment;
};

const SectorUnusualTreemap: React.FC<React.PropsWithChildren<SectorGraphProps>> = ({
  tradesBySector,
  isUnusualFiltered,
  isSweepsFiltered,
  metricMode = 'total',
}) => {
  const isNetMode = metricMode === 'net';
  const chartWrapperRef = React.useRef<HTMLCanvasElement>(null);
  const chartRef = React.useRef<Chart | undefined>(undefined);
  const sectors = React.useMemo(() => {
    return [...tradesBySector].sort((a, b) => {
      if (isNetMode) {
        return Math.abs(b.netPremium) - Math.abs(a.netPremium);
      } else if (isUnusualFiltered) {
        return b.unusualPremium - a.unusualPremium;
      } else if (isSweepsFiltered) {
        return b.sweepPremium - a.sweepPremium;
      } else {
        return b.premium - a.premium;
      }
    });
  }, [tradesBySector, isUnusualFiltered, isSweepsFiltered, isNetMode]);
  const tree = sectors.map((sector, i) => {
    let otm = sector.otm;
    let premium = sector.premium;

    if (isNetMode) {
      premium = Math.abs(sector.netPremium);
    } else if (isUnusualFiltered) {
      otm = sector.unusualOtm;
      premium = sector.unusualPremium;
    } else if (isSweepsFiltered) {
      otm = sector.sweepOtm;
      premium = sector.sweepPremium;
    }

    return {
      sector: sector.sector,
      sentimentScore: isNetMode
        ? sector.netPremiumShare > 0
          ? 100
          : sector.netPremiumShare < 0
          ? -100
          : 0
        : sector.sentimentScore,
      code: i,
      premium: premium,
      OTM: otm,
    };
  });

  React.useEffect(() => {
    const myChartRef = chartRef.current;
    if (myChartRef?.data.datasets) {
      const primaryDataset = myChartRef.data.datasets[0] as SectorTreemapDataset;
      primaryDataset.tree = tree;
      myChartRef.data.labels = sectors.map((s) => s.sector);

      const colorPerSector = tree.map((item) => {
        const maxValue = tree[0].premium;
        const minValue = tree[tree.length - 1].premium;
        const color = mapSectorToBackgroundColor(
          item.sentimentScore,
          isUnusualFiltered,
          isSweepsFiltered,
        );
        const finalColor = colorFromValue({
          color: color,
          value: item.premium,
          border: false,
          maxValue,
          minValue,
        });

        return finalColor;
      });
      primaryDataset.backgroundColor = colorPerSector.map((color) => color);
      primaryDataset.borderColor = colorPerSector.map((color) => color);
      const tooltipCallbacks = myChartRef.options.tooltips?.callbacks;
      if (tooltipCallbacks) {
        tooltipCallbacks.label = function (item, data) {
          return formatTreemapTooltip(
            getTreemapTooltipItem(item, data),
            isNetMode ? 'Net Premium' : 'Premium',
          );
        };
      }
      myChartRef.update();
    }
  }, [sectors, tree, isUnusualFiltered, isSweepsFiltered, isNetMode]);

  React.useEffect(() => {
    const myChartRef = chartWrapperRef.current?.getContext('2d');

    if (myChartRef) {
      chartRef.current = new Chart(myChartRef, {
        type: 'treemap',
        data: {
          datasets: [
            {
              tree,
              key: 'premium',
              groups: ['sector'],
              backgroundColor: function (ctx) {
                const { dataset, dataIndex } = ctx as SectorTreemapContext;
                const chartPoint = dataset.data?.[dataIndex];

                if (!chartPoint || (!dataIndex && dataIndex !== 0)) {
                  return;
                }

                const maxValue = tree[0].premium;
                const minValue = tree[tree.length - 1].premium;

                const color = mapSectorToBackgroundColor(
                  tree[dataIndex].sentimentScore,
                  isUnusualFiltered,
                  isSweepsFiltered,
                );

                const finalColor = colorFromValue({
                  color: color,
                  value: chartPoint.v,
                  border: false,
                  maxValue,
                  minValue,
                });

                return finalColor;
              },
              borderColor: function (ctx) {
                const { dataset, dataIndex } = ctx as SectorTreemapContext;
                const chartPoint = dataset.data?.[dataIndex];

                if (!chartPoint || (!dataIndex && dataIndex !== 0)) {
                  return themeDark.color.accentBlue;
                }

                const maxValue = tree[0].premium;
                const minValue = tree[tree.length - 1].premium;

                const color = mapSectorToBackgroundColor(
                  tree[dataIndex].sentimentScore,
                  isUnusualFiltered,
                  isSweepsFiltered,
                );

                const finalColor = colorFromValue({
                  color: color,
                  value: chartPoint.v,
                  border: false,
                  maxValue,
                  minValue,
                });

                return finalColor;
              },
              spacing: 0.1,
              borderWidth: 2,
              fontColor: themeDark.color.textPrimary,
            } as SectorTreemapDataset,
          ],
        },
        options: {
          maintainAspectRatio: false,
          responsive: true,
          title: {
            display: false,
          },
          legend: {
            display: false,
          },
          tooltips: {
            callbacks: {
              title: function () {
                return '';
              },
              label: function (item, data) {
                return formatTreemapTooltip(
                  getTreemapTooltipItem(item, data),
                  isNetMode ? 'Net Premium' : 'Premium',
                );
              },
            },
          },
        },
      });
    }

    return () => {
      chartRef.current?.destroy();
    };
  }, []);

  return (
    <div
      style={{
        maxHeight: '100%',
        height: '100%',
        width: '100%',
      }}
    >
      <canvas ref={chartWrapperRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );
};

export default SectorUnusualTreemap;
